import React from 'react';
import { Card } from './Card.js';

export type StatCardTone =
  | 'default'
  | 'accent-soft'
  | 'success-soft'
  | 'neutral'
  | 'accent'
  | 'success';

export type StatHintTone =
  | 'default'
  | 'muted'
  | 'accent-soft'
  | 'success-soft'
  | 'accent'
  | 'success';

export interface StatCardDelta {
  value: string | number;
  isPositive?: boolean;
  label?: string;
}

export interface StatCardProps {
  label: string;
  /** Short label shown below md; the desktop label is shown from md up. */
  mobileLabel?: string;
  value: string | number;
  unit?: string;
  hint?: React.ReactNode;
  hintTone?: StatHintTone;
  delta?: StatCardDelta | string | React.ReactNode;
  deltaTone?: 'success' | 'accent' | 'warning' | 'neutral' | 'default';
  icon?: React.ReactNode;
  tone?: StatCardTone;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  mobileLabel,
  value,
  unit,
  hint,
  hintTone = 'muted',
  delta,
  icon,
  tone = 'default',
  className = ''
}) => {
  const valueToneClasses: Record<string, string> = {
    default: 'text-base-content',
    neutral: 'text-base-content',
    'accent-soft': 'text-accent-soft',
    accent: 'text-accent-soft',
    'success-soft': 'text-success-soft',
    success: 'text-success-soft'
  };

  const hintToneClasses: Record<string, string> = {
    default: 'text-base-content/60',
    muted: 'text-base-content/60',
    'accent-soft': 'text-accent-soft',
    accent: 'text-accent-soft',
    'success-soft': 'text-success-soft',
    success: 'text-success-soft'
  };

  const renderDelta = () => {
    if (!delta) return null;

    if (React.isValidElement(delta)) {
      return delta;
    }

    if (typeof delta === 'object' && 'value' in delta) {
      const d = delta as StatCardDelta;
      const isPos =
        d.isPositive ?? (typeof d.value === 'string' && d.value.trim().startsWith('+'));
      const pillToneClass = isPos
        ? 'bg-success/12 text-success-soft border border-success/30'
        : 'bg-error/12 text-error-soft border border-error/30';

      return (
        <span className="inline-flex items-center gap-1.5">
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${pillToneClass}`}
          >
            <span dir="ltr" className="ltr-nums font-mono tabular-nums">
              {d.value}
            </span>
          </span>
          {d.label && <span className="text-xs text-base-content/60">{d.label}</span>}
        </span>
      );
    }

    const deltaStr = String(delta);
    const isPos = deltaStr.trim().startsWith('+');
    const pillToneClass = isPos
      ? 'bg-success/12 text-success-soft border border-success/30'
      : 'bg-error/12 text-error-soft border border-error/30';

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${pillToneClass}`}
      >
        <span dir="ltr" className="ltr-nums font-mono tabular-nums">
          {deltaStr}
        </span>
      </span>
    );
  };

  return (
    <Card
      padding="none"
      className={`flex flex-col gap-1.5 md:gap-2 p-3.5 md:px-5 md:py-[18px] rounded-[14px] md:rounded-2xl ${className}`}
    >
      <div className="flex items-center justify-between text-xs md:text-[13px] text-base-content/60 font-normal">
        {mobileLabel ? (
          <>
            <span className="md:hidden">{mobileLabel}</span>
            <span className="hidden md:inline">{label}</span>
          </>
        ) : (
          <span>{label}</span>
        )}
        {icon && <span className="text-base-content/40 shrink-0">{icon}</span>}
      </div>

      <div className="flex items-baseline gap-2 min-h-[35px] md:min-h-0">
        <span
          dir="ltr"
          className={`ltr-nums font-mono tabular-nums text-[22px] md:text-[28px] md:leading-[1.2] font-bold ${
            valueToneClasses[tone] ?? 'text-base-content'
          }`}
        >
          {value}
        </span>
        {unit && (
          <span className="text-xs md:text-[13px] text-base-content/60 font-medium">{unit}</span>
        )}
      </div>

      {(delta || hint) && (
        <div className="hidden md:flex items-center gap-2 flex-wrap">
          {renderDelta()}
          {hint && (
            <span
              className={`text-xs leading-5 ${hintToneClasses[hintTone] ?? 'text-base-content/60'}`}
            >
              {hint}
            </span>
          )}
        </div>
      )}
    </Card>
  );
};

StatCard.displayName = 'StatCard';
